

import React, { useEffect, useState } from "react";
import { useAuth } from "../App";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { ChevronDown, ChevronRight } from "lucide-react";

export default function AccessRequestsAdmin() {
  const { axiosInstance, user } = useAuth();
  const [requests, setRequests] = useState([]);
  const [loadingId, setLoadingId] = useState(null);
  const [generatedCode, setGeneratedCode] = useState({});
  const [open, setOpen] = useState(false);

  const isAdmin =
    user.role === "admin" || user.care_role === "coordinateur";

  const roleLabels = {
    caregiver: "Aide-soignant",
    nurse: "Infirmier",
  };

  const fetchRequests = async () => {
    try {
      const res = await axiosInstance.get("/access-request");
      setRequests(res.data);
    } catch {
      toast.error("Erreur chargement demandes");
    }
  };

  useEffect(() => {
    if (isAdmin) fetchRequests();
  }, [isAdmin]);

  const handleGenerateCode = async (requestId) => {
    try {
      setLoadingId(requestId);

      const res = await axiosInstance.post(
        "/access-request/generate-code",
        {
          request_id: requestId,
        }
      );

      setGeneratedCode((prev) => ({
        ...prev,
        [requestId]: res.data.access_code,
      }));

      toast.success("Code généré");
      fetchRequests();
    } catch (err) {
      toast.error(
        err.response?.data?.detail || "Erreur génération code"
      );
    } finally {
      setLoadingId(null);
    }
  };

  if (!isAdmin) return null;

  const pendingCount = requests.filter(
    (r) => r.status === "pending"
  ).length;

  return (
    <Card className="border-[#D4EBEB] bg-white shadow-sm">
      <CardContent className="p-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="
            flex w-full items-center justify-between
            rounded-lg px-2 py-2
            transition
            hover:bg-[#F1F8F8]
          "
        >
          <div className="flex items-center gap-2">
            {open ? (
              <ChevronDown
                size={18}
                className="text-[#4F7777]"
              />
            ) : (
              <ChevronRight
                size={18}
                className="text-[#4F7777]"
              />
            )}

            <div className="text-left">
              <h2 className="text-sm font-semibold text-[#294B4B]">
                Demandes d’accès
              </h2>

              <p className="text-xs text-slate-500">
                Gestion admin / coordination
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {pendingCount > 0 && (
              <span
                className="
                  rounded-full
                  bg-[#D4EBEB]
                  px-2 py-1
                  text-xs font-medium
                  text-[#355E5E]
                  border border-[#B5DADA]
                "
              >
                {pendingCount} en attente
              </span>
            )}

            <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">
              {requests.length}
            </span>
          </div>
        </button>

        {open && (
          <div className="mt-3 space-y-2">
            {requests.length === 0 ? (
              <p className="px-2 py-3 text-sm text-slate-500">
                Aucune demande pour le moment.
              </p>
            ) : (
              requests.map((r) => (
                <div
                  key={r._id}
                  className="
                    rounded-lg
                    border border-[#D4EBEB]
                    bg-[#F8FCFC]
                    p-3
                  "
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-slate-900">
                        {r.user_name}
                      </p>

                      <p className="truncate text-xs text-slate-500">
                        {r.user_email}
                      </p>
                    </div>

                    <span
                      className={`w-fit rounded-full border px-2 py-1 text-xs font-medium ${
                        r.status === "pending"
                          ? "bg-[#D4EBEB] text-[#355E5E] border-[#B5DADA]"
                          : r.status === "approved"
                          ? "bg-green-100 text-green-800 border-green-200"
                          : "bg-slate-100 text-slate-700 border-slate-200"
                      }`}
                    >
                      {r.status === "pending"
                        ? "En attente"
                        : r.status === "approved"
                        ? "Approuvée"
                        : r.status}
                    </span>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-1 text-xs text-slate-600 md:grid-cols-4">
                    <p>
                      <span className="font-medium text-slate-800">
                        Rôle :
                      </span>{" "}
                      {roleLabels[r.role] || r.role}
                    </p>

                    <p>
                      <span className="font-medium text-slate-800">
                        Date :
                      </span>{" "}
                      {r.created_at
                        ? new Date(
                            r.created_at
                          ).toLocaleDateString("fr-BE")
                        : "-"}
                    </p>

                    <p>
                      <span className="font-medium text-slate-800">
                        Code :
                      </span>{" "}
                      <span className="font-semibold text-[#4F7777]">
                        {generatedCode[r._id] ||
                          r.access_code ||
                          "-"}
                      </span>
                    </p>
                  </div>

                  {r.status === "pending" && (
                    <div className="mt-3 flex justify-end">
                      <Button
                        size="sm"
                        onClick={() =>
                          handleGenerateCode(r._id)
                        }
                        disabled={loadingId === r._id}
                        className="
                          bg-[#5F8989]
                          hover:bg-[#4F7777]
                          text-white
                          shadow-sm
                        "
                      >
                        {loadingId === r._id
                          ? "Génération..."
                          : "Générer le code"}
                      </Button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}